import { css } from '@emotion/core'
import parse from 'html-react-parser'
import React from 'react'

import { ctaInlineBreakPoint } from '../../constants/breakPoints'
import { breakpoints } from '../../constants/breakPoints'
import { isMobile } from '../../helpers/common'
import { useHsFormCallback } from '../../hooks/useHsFormCallback'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import { CTANewsletterProps } from '../../interfaces/cta'
import { Button } from '../Button'
import { HubspotForm } from '../HubspotForm'
import Image from '../Image'

const CTANewsletter: React.FunctionComponent<CTANewsletterProps> = ({
  id,
  blackBG = false,
  isTextCard = false,
  isSidebarCta = false,
}) => {
  const setBlogSubscribed = useLocalStorage('blog_subscribed', false)[1]

  useHsFormCallback({
    onFormSubmittedCallback: () => setBlogSubscribed(true),
    hubspotFormIds: ['4355572f-13e9-45a7-8d84-10e05c3b0967', 'afc63de9-b452-4473-b3c3-f1f8df035204'],
  })

  return (
    <div
      id={id}
      className={`purple${isTextCard ? '-gradient' : ''}-bg with-box-shadow`}
      css={containerStyle(isTextCard, isSidebarCta)}
    >
      <div css={imageStyle(isTextCard, isSidebarCta)}>
        <Image
          src={`cta/paper-plane${isTextCard || isSidebarCta || isMobile(breakpoints.small) ? '-small' : ''}.webp`}
          alt="Product Digest newsletter signup"
        />
      </div>
      {!isTextCard && (
        <div className={`${isSidebarCta ? 'strong-body-1' : 'heading4'} blue-text`} css={blueTextStyle}>
          Newsletter
        </div>
      )}
      <h2
        className={`${isSidebarCta ? 'heading3' : 'heading2'} black-text no-margin ${isTextCard && 'center'}`}
        css={titleStyle}
      >
        {parse(
          isTextCard || isSidebarCta
            ? 'Join 100,000+ <br /> product leaders'
            : 'Cut through the clutter of <br class="hide-on-small" /> <span class="underlined-content">PM Content</span> with our <br class="hide-on-small" /> bi-weekly digest'
        )}
      </h2>
      {isSidebarCta ? (
        <div css={hubspotFormStyle(isSidebarCta, isTextCard, blackBG)}>
          <HubspotForm hubspotFormId="afc63de9-b452-4473-b3c3-f1f8df035204" fullWidthButton />
        </div>
      ) : isTextCard ? (
        <>
          <div className="subtitle center left-on-small" css={subtitleStyle}>
            Bi-weekly top stories about <br /> tactics, tools and innovations.
          </div>
          <div css={hubspotFormStyle(isSidebarCta, isTextCard, blackBG)}>
            <HubspotForm hubspotFormId="afc63de9-b452-4473-b3c3-f1f8df035204" fullWidthButton />
          </div>
        </>
      ) : (
        <Button link={`/cta/${isSidebarCta ? 'sidebar' : 'inline'}/newsletter/`} css={buttonStyle}>
          Sign up
        </Button>
      )}
    </div>
  )
}

export default CTANewsletter

const containerStyle = (isTextCard: boolean, isSidebarCta: boolean) => css`
  position: relative;
  border-radius: 0.5rem;

  ${isSidebarCta &&
  `
    top: 6.5rem;
    position: sticky;
    margin: 0 !important;
    padding: 1.25rem 1rem !important;
  `};

  ${isTextCard
    ? `
    padding: 3rem 1rem 4rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
  `
    : `
    margin: 2rem 0;
    padding: 3rem;

    @media screen and (max-width: ${breakpoints.xlarge}px) {
      padding: 2rem 3rem;
    }

    @media screen and (max-width: ${ctaInlineBreakPoint}px) {
      padding: 2rem 2.4rem;
    }

    @media screen and (max-width: ${breakpoints.medium}px) {
      padding: 2rem 3rem;
    }
    
    @media screen and (max-width: ${breakpoints.small}px) {
      margin-top: 3rem;
      padding: 2rem;
    }
  `};
`

const imageStyle = (isTextCard: boolean, isSidebarCta: boolean) => css`
  ${isTextCard
    ? `
      margin: 0 auto 0.5rem;
      width: 5rem;
    `
    : `
      position: absolute;
      bottom: 0;
      right: 0;
      width: 55%;

      ${
        isSidebarCta &&
        `
        width: 64px !important;
        top: -30px;
        right: 8px;
      `
      }

      @media screen and (max-width: ${breakpoints.xlarge}px) {
        width: 50%;
      }

      @media screen and (max-width: ${ctaInlineBreakPoint}px) and (min-width: ${breakpoints.medium + 1}px) {
        width: 55%;
      }

      @media screen and (max-width: ${breakpoints.small}px) {
        width: 5rem;
        top: -2rem;
        right: 1.5rem;
      }
    `}

  @media screen and (max-width: ${breakpoints.small}px) {
    margin: 0;
  }
`

// export for CTAProductWalkthrough
export const blueTextStyle = css`
  margin-bottom: 0.25rem;
`

const titleStyle = css`
  .underlined-content {
    white-space: nowrap;
    padding: 30px 0 8px;
    position: relative;
    z-index: 1;
  }

  .underlined-content::before {
    content: '';
    position: absolute;
    /* left: 7%; */
    bottom: 12px;
    width: 100%;
    height: 5px;
    transform: skew(-40deg) rotate(-2deg) translateX(-3%) translateZ(20px);
    background: #89d5ff;
    z-index: -1;
  }

  @media screen and (max-width: ${ctaInlineBreakPoint}px) and (min-width: ${breakpoints.medium + 1}px) {
    br {
      display: none;
    }
  }
`

const subtitleStyle = css`
  margin-top: 1rem !important;
`

const hubspotFormStyle = (isSidebarCta: boolean, isTextCard: boolean, blackBG: boolean) => css`
  form {
    margin: ${isSidebarCta ? '0.5rem 0 0' : ' 0 1rem'};
    gap: 0.5rem;

    .legal-consent-container {
      font-size: 0.625rem !important;
      ${isSidebarCta ? 'margin: 0 0.125rem;' : 'text-align: center; margin-bottom: 0.75rem;'};
    }

    ${blackBG && '.input > input { color: #0a1c33 !important }'};

    @media screen and (max-width: ${breakpoints.small}px) {
      margin: 0;
    }
  }

  ${isTextCard && '.submitted-message {text-align: center}'};
`

const buttonStyle = css`
  margin-top: 1.5rem;

  @media screen and (max-width: ${ctaInlineBreakPoint}px) {
    margin-top: 1.5rem;
  }
`
